import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Product } from '../services/api';
import { apiService } from '../services/api';
import ChoicesSelect from './ChoicesSelect';
import useClickOutside from '../hooks/useClickOutside';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  product?: Product | null;
  onSave: (productData: any) => void;
}

interface ProductSize {
  name: string;
  stock_quantity: number;
  reserved_quantity: number;
  price?: number;
  compare_at_price?: number;
  weight?: number;
  barcode?: string;
  is_active: boolean;
  track_inventory: boolean;
}

interface ProductVariant {
  id?: number;
  sku?: string;
  color: string;
  color_code?: string;
  sizes: ProductSize[];
  price?: number;
  compare_at_price?: number;
  is_active: boolean;
}

interface FormData {
  product_code: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  category_id: string;
  brand_id: string;
  product_images: string[];
  hover_image: string;
  features: string[];
  is_active: boolean;
  is_featured: boolean;
  is_best_seller: boolean;
  is_trending: boolean;
  show_on_website: boolean;
  tags: string[];
  variants: ProductVariant[];
  promotion_id: string;
  low_stock_threshold: string;
}

interface Category {
  id: number;
  name: string;
  full_path?: string;
  level?: number;
  is_parent?: boolean;
  parent_id?: number | null;
}

interface Brand {
  id: number;
  name: string;
}



interface Promotion {
  id: number;
  name: string;
  type: string;
  value: number;
  is_active: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  mode,
  product,
  onSave
}) => {
  const modalRef = useClickOutside(onClose);
  // Explicitly type the mode to fix TypeScript comparison issues
  const modalMode = mode as 'create' | 'edit' | 'view';
  const [formData, setFormData] = useState<FormData>({
    product_code: '',
    name: '',
    slug: '',
    description: '',
    price: '',
    category_id: '',
    brand_id: '',

    product_images: [],
    hover_image: '',
    features: [],
    is_active: true,
    is_featured: false,
    is_best_seller: false,
    is_trending: false,
    show_on_website: true,
    tags: [],
    variants: [],
    promotion_id: '',
    low_stock_threshold: '10'
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingHoverImage, setUploadingHoverImage] = useState(false);

  // Load form data when product changes
  useEffect(() => {
    if (product && (mode === 'edit' || mode === 'view')) {
      setFormData({
        product_code: product.product_code || '',
        name: product.name || '',
        slug: product.slug || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        category_id: product.category_id?.toString() || '',
        brand_id: product.brand_id?.toString() || '',

        product_images: product.product_images || [],
        hover_image: product.hover_image || '',
        features: product.features || [],
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        is_best_seller: product.is_best_seller ?? false,
        is_trending: product.is_trending ?? false,
        show_on_website: product.show_on_website ?? true,
        tags: product.tags || [],
        variants: product.variants || [],
        promotion_id: product.applied_promotion_id?.toString() || product.active_promotions?.[0]?.id?.toString() || '',
        low_stock_threshold: (product.low_stock_threshold ?? 10).toString()
      });
    } else if (mode === 'create') {
      // Reset form for create mode
      setFormData({
        product_code: '',
        name: '',
        slug: '',
        description: '',
        price: '',
        category_id: '',
        brand_id: '',

        product_images: [],
        hover_image: '',
        features: [],
        is_active: true,
        is_featured: false,
        is_best_seller: false,
        is_trending: false,
        show_on_website: true,
        tags: [],
        variants: [],
        promotion_id: '',
        low_stock_threshold: '10'
      });
    }
  }, [product, mode]);

  // Load categories, brands, and promotions
  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  const loadDropdownData = async () => {
    try {
      const [categoriesRes, brandsRes, promotionsRes] = await Promise.all([
        apiService.get('/admin/categories/hierarchical'),
        apiService.get('/admin/brands', { 
          params: { 
            per_page: 100, 
            _t: Date.now() 
          },
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }),
        apiService.get('/admin/promotions')
      ]);

      setCategories(categoriesRes.data?.data || categoriesRes.data || []);
      setBrands(brandsRes.data?.data || brandsRes.data || []);
      setPromotions(promotionsRes.data?.data || promotionsRes.data || []);
    } catch (error) {
      // Error loading dropdown data
    }
  };

  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug when name changes
    if (field === 'name' && mode === 'create') {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleMultipleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate file sizes (20MB max per image)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    const oversizedFiles = Array.from(files).filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        product_images: `The following files are too large (max 20MB each): ${oversizedFiles.map(f => f.name).join(', ')}`
      }));
      return;
    }

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const invalidFiles = Array.from(files).filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        product_images: 'Invalid file types. Only JPEG, PNG, JPG, GIF, and WebP are allowed.'
      }));
      return;
    }

    // Clear any previous errors
    setErrors(prev => ({ ...prev, product_images: '' }));

    setUploadingImages(true);
    try {
      const formDataUpload = new FormData();
      Array.from(files).forEach(file => {
        formDataUpload.append('images[]', file);
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/products/upload-multiple-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });

      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          product_images: [...prev.product_images, ...result.images.map((img: any) => img.url)]
        }));
        // Clear any previous errors on success
        setErrors(prev => ({ ...prev, product_images: '' }));
      } else {
        // Display detailed error information
        let errorMessage = result.message || 'Upload failed';
        if (result.debug) {
          errorMessage += ` (Server limits: upload_max_filesize=${result.debug.upload_max_filesize}, post_max_size=${result.debug.post_max_size})`;
        }
        setErrors(prev => ({
          ...prev,
          product_images: errorMessage
        }));
      }
    } catch (error) {
      // Error uploading images
    } finally {
      setUploadingImages(false);
    }
  };

  const handleHoverImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (20MB max)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        hover_image: 'File is too large. Maximum size is 20MB.'
      }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        hover_image: 'Invalid file type. Only JPEG, PNG, JPG, GIF, and WebP are allowed.'
      }));
      return;
    }

    // Clear any previous errors
    setErrors(prev => ({ ...prev, hover_image: '' }));

    setUploadingHoverImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/admin/products/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });

      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          hover_image: result.url
        }));
        // Clear any previous errors on success
        setErrors(prev => ({ ...prev, hover_image: '' }));
      } else {
        // Display detailed error information
        let errorMessage = result.message || 'Upload failed';
        if (result.debug) {
          errorMessage += ` (Server limits: upload_max_filesize=${result.debug.upload_max_filesize}, post_max_size=${result.debug.post_max_size})`;
        }
        setErrors(prev => ({
          ...prev,
          hover_image: errorMessage
        }));
      }
    } catch (error) {
      // Error uploading hover image
    } finally {
      setUploadingHoverImage(false);
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      product_images: prev.product_images.filter((_, i) => i !== index)
    }));
  };

  const removeHoverImage = () => {
    setFormData(prev => ({
      ...prev,
      hover_image: ''
    }));
  };

  const handleArrayInputChange = (field: 'features' | 'tags', value: string) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    handleInputChange(field, items);
  };

  // Feature list management functions
  const addFeature = () => {
    const newFeatures = [...formData.features, ''];
    handleInputChange('features', newFeatures);
  };

  const removeFeature = (index: number) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    handleInputChange('features', newFeatures);
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    handleInputChange('features', newFeatures);
  };

  const addVariant = () => {
    const newVariant: ProductVariant = {
      color: '',
      color_code: '',
      sizes: [{
        name: '',
        stock_quantity: 0,
        reserved_quantity: 0,
        price: undefined,
        compare_at_price: undefined,
        weight: undefined,
        barcode: '',
        is_active: true,
        track_inventory: true
      }],
      price: undefined,
      compare_at_price: undefined,
      is_active: true,
    };
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));
  };

  const removeVariant = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Size management functions
  const addSizeToVariant = (variantIndex: number) => {
    const newSize: ProductSize = {
      name: '',
      stock_quantity: 0,
      reserved_quantity: 0,
      price: undefined,
      compare_at_price: undefined,
      weight: undefined,
      barcode: '',
      is_active: true,
      track_inventory: true
    };
    
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => {
        if (i === variantIndex) {
          return {
            ...variant,
            sizes: [...variant.sizes, newSize]
          };
        }
        return variant;
      })
    }));
  };

  const removeSizeFromVariant = (variantIndex: number, sizeIndex: number) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => {
        if (i === variantIndex) {
          return {
            ...variant,
            sizes: variant.sizes.filter((_, si) => si !== sizeIndex)
          };
        }
        return variant;
      })
    }));
  };

  const handleSizeChange = (variantIndex: number, sizeIndex: number, field: keyof ProductSize, value: any) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => {
        if (i === variantIndex) {
          return {
            ...variant,
            sizes: variant.sizes.map((size, si) => {
              if (si === sizeIndex) {
                return { ...size, [field]: value };
              }
              return size;
            })
          };
        }
        return variant;
      })
    }));
  };

  const handleVariantChange = (index: number, field: keyof ProductVariant, value: any) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => {
        if (i === index) {
          const updatedVariant = { ...variant, [field]: value };
          
          // Auto-generate color_code when color changes
          if (field === 'color' && value) {
            // Generate a simple hex color based on color name
            const colorMap: Record<string, string> = {
              'red': '#FF0000',
              'blue': '#0000FF',
              'green': '#00FF00',
              'yellow': '#FFFF00',
              'black': '#000000',
              'white': '#FFFFFF',
              'gray': '#808080',
              'grey': '#808080',
              'pink': '#FFC0CB',
              'purple': '#800080',
              'orange': '#FFA500',
              'brown': '#A52A2A',
              'navy': '#000080',
              'maroon': '#800000',
              'teal': '#008080'
            };
            
            const colorName = value.toLowerCase().trim();
            updatedVariant.color_code = colorMap[colorName] || '#000000';
          }
          
          return updatedVariant;
        }
        return variant;
      })
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.price || Number(formData.price) <= 0) newErrors.price = 'Valid price is required';
    if (!formData.category_id) newErrors.category_id = 'Category is required';
    if (!formData.brand_id) newErrors.brand_id = 'Brand is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      // Calculate total stock from all sizes in all variants
      const totalStock = formData.variants.reduce((total, variant) => {
        const variantStock = variant.sizes.reduce((variantTotal, size) => {
          return variantTotal + (parseInt(size.stock_quantity?.toString() || '0') || 0);
        }, 0);
        return total + variantStock;
      }, 0);

      const payload = {
        ...formData,
        price: Number(formData.price),
        category_id: parseInt(formData.category_id),
        brand_id: parseInt(formData.brand_id),
        in_stock: totalStock, // Add calculated stock to payload
        low_stock_threshold: formData.low_stock_threshold === '' ? 10 : parseInt(formData.low_stock_threshold, 10),
        // For create: send null when empty to satisfy exists rule; For edit: backend expects string, send '' when removing
        promotion_id:
          modalMode === 'create'
            ? (formData.promotion_id && formData.promotion_id !== ''
                ? String(formData.promotion_id)
                : null)
            : (formData.promotion_id && formData.promotion_id !== ''
                ? String(formData.promotion_id)
                : ''),
        // Ensure variant data is properly formatted for multi-size structure
        variants: formData.variants.map(variant => ({
          ...variant,
          price: variant.price ? Number(variant.price) : undefined,
          compare_at_price: variant.compare_at_price ? Number(variant.compare_at_price) : undefined,
          sizes: variant.sizes.map(size => ({
            ...size,
            stock_quantity: parseInt(size.stock_quantity?.toString() || '0') || 0,
            reserved_quantity: parseInt(size.reserved_quantity?.toString() || '0') || 0,
            price: size.price ? Number(size.price) : undefined,
            compare_at_price: size.compare_at_price ? Number(size.compare_at_price) : undefined,
            weight: size.weight ? Number(size.weight) : undefined
          }))
        }))
      };

      // Pass the payload to parent component to handle API call
      onSave(payload);
      onClose();
    } catch (error: any) {
      // Error saving product
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {modalMode === 'create' ? 'Add New Product' : 
             modalMode === 'edit' ? 'Edit Product' : 'View Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {modalMode === 'view' ? (
          /* Enhanced Product View Design */
          <div className="bg-gray-50">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative p-8">
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-8">
                    <div className="flex items-center space-x-3 mb-2">
                      <h1 className="text-3xl font-bold">{formData.name}</h1>
                      <div className="flex space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          formData.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}>
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {formData.is_featured && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-500 text-white">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Featured
                          </span>
                        )}
                      </div>
                    </div>
                    {formData.product_code && (
                      <div className="mb-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-200 border border-blue-400/30">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                          </svg>
                          {formData.product_code}
                        </span>
                      </div>
                    )}
                    <p className="text-gray-200 text-lg mb-6 leading-relaxed">{formData.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-400">{formData.price ? Number(formData.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0'}</div>
                        <div className="text-sm text-gray-300">Myanmar Kyat</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className={`text-2xl font-bold ${
                          formData.variants.reduce((total, variant) => {
                            const variantStock = variant.sizes.reduce((sizeTotal, size) => sizeTotal + (parseInt(size.stock_quantity?.toString() || '0') || 0), 0);
                            return total + variantStock;
                          }, 0) > 0
                            ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {formData.variants.reduce((total, variant) => {
                            const variantStock = variant.sizes.reduce((sizeTotal, size) => sizeTotal + (parseInt(size.stock_quantity?.toString() || '0') || 0), 0);
                            return total + variantStock;
                          }, 0)}
                        </div>
                        <div className="text-sm text-gray-300">Total Stock</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">{formData.variants.length}</div>
                        <div className="text-sm text-gray-300">Variants</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-purple-400">{formData.variants.reduce((total, variant) => total + variant.sizes.length, 0)}</div>
                        <div className="text-sm text-gray-300">Total Sizes</div>
                      </div>
                    </div>
                  </div>
                  {formData.product_images && formData.product_images.length > 0 && (
                    <div className="flex-shrink-0">
                      <div className="relative group">
                        <img
                          src={formData.product_images[0]}
                          alt={formData.name}
                          className="w-48 h-48 object-cover rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="text-sm font-medium">Primary Image</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-8 space-y-8">
              {/* Quick Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Category & Brand */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Classification
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium text-blue-900">Category</span>
                      <span className="text-sm text-blue-700 bg-blue-100 px-3 py-1.5 rounded-lg font-medium">
                        {categories.find(c => c.id.toString() === formData.category_id)?.full_path || 
                         categories.find(c => c.id.toString() === formData.category_id)?.name || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                      <span className="text-sm font-medium text-orange-900">Brand</span>
                      <span className="text-sm text-orange-700 bg-orange-100 px-3 py-1.5 rounded-lg font-medium">
                        {brands.find(b => b.id.toString() === formData.brand_id)?.name || 'N/A'}
                      </span>
                    </div>

                    {promotions.find(p => p.id.toString() === formData.promotion_id) && (
                      <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                        <span className="text-sm font-medium text-indigo-900">Promotion</span>
                        <span className="text-sm text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-lg font-medium">
                          {promotions.find(p => p.id.toString() === formData.promotion_id)?.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Product Features
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {formData.features?.length || 0}
                      </span>
                    </h3>
                  </div>
                  {formData.features && formData.features.length > 0 ? (
                    <div className="space-y-3 max-h-40 overflow-y-auto">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                          <svg className="w-4 h-4 mr-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-green-900 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Features Available</h4>
                      <p className="text-gray-500">This product doesn't have any features configured.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Images Gallery */}
              {formData.product_images && formData.product_images.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      <svg className="w-6 h-6 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Product Gallery
                      <span className="ml-3 text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">
                        {formData.product_images.length} {formData.product_images.length === 1 ? 'Image' : 'Images'}
                      </span>
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {formData.product_images.map((image, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 group-hover:border-purple-300 transition-all duration-300">
                            <img
                              src={image}
                              alt={`Product ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          {index === 0 && (
                            <div className="absolute -top-2 -left-2">
                              <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                                PRIMARY
                              </span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute bottom-2 left-2 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="text-xs font-medium">Image {index + 1}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.hover_image && (
                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <svg className="w-4 h-4 mr-2 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Hover Effect Image
                        </h4>
                        <div className="relative inline-block group">
                          <div className="w-32 h-32 rounded-xl overflow-hidden bg-gray-100 border-2 border-pink-200 group-hover:border-pink-400 transition-colors">
                            <img
                              src={formData.hover_image}
                              alt={`${formData.name} hover view`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="absolute -top-2 -right-2">
                            <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                              HOVER
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Product Variants */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-3 py-2 border-b border-gray-100">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Product Variants
                    <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-md font-medium">
                      {formData.variants.length}
                    </span>
                  </h3>
                </div>
                <div className="p-3">
                  {formData.variants.length > 0 ? (
                    <div className="space-y-3">
                      {formData.variants.map((variant, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div
                                className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: variant.color_code || '#6B7280' }}
                                title={variant.color}
                              ></div>
                              <div className="text-sm font-semibold text-gray-900">{variant.color}</div>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                {variant.sizes.length} {variant.sizes.length === 1 ? 'size' : 'sizes'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              SKU: {variant.sku || 'Auto-generated'}
                            </div>
                          </div>
                          
                          {variant.sizes.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {variant.sizes.map((size, sizeIndex) => (
                                <div key={sizeIndex} className="bg-white rounded border border-gray-200 p-2">
                                  <div className="text-xs font-medium text-gray-900 mb-1 text-center">
                                    {size.name}
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="bg-green-50 rounded p-1 text-center">
                                      <div className="font-semibold text-green-700">
                                        {size.price ? `${Number(size.price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : Number(formData.price || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Ks
                                      </div>
                                    </div>
                                    <div className={`rounded p-1 text-center ${
                                      (size.stock_quantity || 0) > 0 
                                        ? 'bg-blue-50' 
                                        : 'bg-red-50'
                                    }`}>
                                      <div className={`font-semibold ${
                                        (size.stock_quantity || 0) > 0 
                                          ? 'text-blue-700' 
                                          : 'text-red-700'
                                      }`}>
                                        {size.stock_quantity || 0}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex justify-center mt-1">
                                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs ${
                                      size.is_active 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                      <div className={`w-1 h-1 rounded-full mr-1 ${
                                        size.is_active ? 'bg-green-500' : 'bg-red-500'
                                      }`}></div>
                                      {size.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-2 text-gray-500 text-sm">
                              No sizes configured for this variant
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                              variant.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                variant.is_active ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              Variant {variant.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <div className="text-xs text-gray-600">
                              Total Stock: <span className="font-semibold">
                                {variant.sizes.reduce((total, size) => total + (size.stock_quantity || 0), 0)} units
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">No Variants Available</h4>
                      <p className="text-xs text-gray-500">Add color and size variants for this product.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              {formData.tags && formData.tags.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      <svg className="w-6 h-6 mr-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Product Tags
                      <span className="ml-3 text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium">
                        {formData.tags.length} {formData.tags.length === 1 ? 'Tag' : 'Tags'}
                      </span>
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-3">
                      {formData.tags.map((tag, index) => {
                        const colors = [
                          'bg-blue-100 text-blue-800 border-blue-200',
                          'bg-green-100 text-green-800 border-green-200',
                          'bg-purple-100 text-purple-800 border-purple-200',
                          'bg-pink-100 text-pink-800 border-pink-200',
                          'bg-indigo-100 text-indigo-800 border-indigo-200',
                          'bg-red-100 text-red-800 border-red-200',
                          'bg-yellow-100 text-yellow-800 border-yellow-200',
                          'bg-gray-100 text-gray-800 border-gray-200'
                        ];
                        const colorClass = colors[index % colors.length];
                        return (
                          <span key={index} className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${colorClass} hover:scale-105 transition-transform duration-200 cursor-default`}>
                            <svg className="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                            {tag}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Form for Create/Edit modes */
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Code
              </label>
              <input
                type="text"
                placeholder="Leave empty to auto-generate (e.g., SIG2025001)"
                value={formData.product_code}
                onChange={(e) => handleInputChange('product_code', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100"
              />
              <p className="text-gray-500 text-xs mt-1">Auto-generated if left empty</p>
              {errors.product_code && <p className="text-red-500 text-sm mt-1">{errors.product_code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none focus:ring-0"
                placeholder="auto-generated-from-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (Ks) *
              </label>
              <input
                type="number"
                placeholder="Enter price in Kyats"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100"
              />
              {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}

              {/* Low stock threshold */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Low Stock Alert Threshold
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="10"
                  value={formData.low_stock_threshold}
                  onChange={(e) => handleInputChange('low_stock_threshold', e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Product is flagged "low stock" when total stock falls to this number or below.
                </p>
              </div>

              {/* Show promotional price if product has promotions */}
              {mode === 'view' && product?.has_promotion && product?.discounted_price && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {product.promotional_price ? 'Saved Promotional Price' : 'Current Promotional Price'}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-lg font-bold text-red-600">
                          {product.discounted_price.toLocaleString()} Ks
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          -{product.discount_percentage?.toFixed(0)}% OFF
                        </span>
                      </div>
                      <p className="text-xs text-red-600 mt-1">
                        Original: <span className="line-through">{product.price.toLocaleString()} Ks</span>
                      </p>
                      {product.promotional_price && product.promotional_price_set_at && (
                        <p className="text-xs text-red-500 mt-1">
                          Price saved on: {new Date(product.promotional_price_set_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          {/* Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <ChoicesSelect
                options={categories.map(category => ({
                  value: category.id.toString(),
                  label: category.level === 0 ? category.name : `└─ ${category.name}`,
                  customProperties: {
                    level: category.level,
                    'data-level': category.level
                  }
                }))}
                value={formData.category_id}
                onChange={(value) => handleInputChange('category_id', value)}
                placeholder="Select Category"
                disabled={mode === 'view'}
                className="w-full"
              />
              {errors.category_id && <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand *
              </label>
              <ChoicesSelect
                options={brands.map(brand => ({
                  value: brand.id.toString(),
                  label: brand.name
                }))}
                value={formData.brand_id}
                onChange={(value) => handleInputChange('brand_id', value)}
                placeholder="Select Brand"
                disabled={mode === 'view'}
                className="w-full"
              />
              {errors.brand_id && <p className="text-red-500 text-sm mt-1">{errors.brand_id}</p>}
            </div>


          </div>

          {/* Promotions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Promotion
            </label>
            <ChoicesSelect
              options={promotions.filter(promotion => promotion.is_active).map(promotion => ({
                value: promotion.id.toString(),
                label: `${promotion.name} (${promotion.type === 'percentage' ? `${promotion.value}%` : `${promotion.value} Ks`} off)`
              }))}
              value={formData.promotion_id}
              onChange={(value) => handleInputChange('promotion_id', value)}
              placeholder="No Promotion"
              disabled={mode === 'view'}
              className="w-full"
            />
            
            {/* Show selected promotion details */}
            {formData.promotion_id && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                {(() => {
                  const selectedPromotion = promotions.find(p => p.id.toString() === formData.promotion_id);
                  if (!selectedPromotion) return null;
                  
                  return (
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        Selected: {selectedPromotion.name}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        {selectedPromotion.type === 'percentage' 
                          ? `${selectedPromotion.value}% discount` 
                          : selectedPromotion.type === 'fixed_amount'
                          ? `${selectedPromotion.value} Ks fixed discount`
                          : 'Buy X Get Y promotion'
                        }
                      </p>
                      {formData.price && selectedPromotion.type !== 'buy_x_get_y' && (
                        <p className="text-xs text-blue-600 mt-1">
                          Promotional price: {
                            selectedPromotion.type === 'percentage'
                              ? Number(Number(formData.price) * (1 - selectedPromotion.value / 100)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                              : Math.max(0, Number(formData.price) - selectedPromotion.value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                          } Ks
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Product Variants */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="bg-gray-50 border-b border-gray-200 p-4 rounded-t-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Product Variants
                  </h3>
                  <span className="text-sm bg-gray-200 text-gray-700 px-2 py-1 rounded font-medium">
                    {formData.variants.length} {formData.variants.length === 1 ? 'variant' : 'variants'}
                  </span>
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <span className={`font-medium ${
                      formData.variants.reduce((total, variant) => {
                        const variantStock = variant.sizes.reduce((sizeTotal, size) => sizeTotal + (parseInt(size.stock_quantity?.toString() || '0') || 0), 0);
                        return total + variantStock;
                      }, 0) > 0 
                        ? 'text-green-700' 
                        : 'text-red-700'
                    }`}>
                      {formData.variants.reduce((total, variant) => {
                        const variantStock = variant.sizes.reduce((sizeTotal, size) => sizeTotal + (parseInt(size.stock_quantity?.toString() || '0') || 0), 0);
                        return total + variantStock;
                      }, 0)} units
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {formData.variants.length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h4 className="text-base font-semibold text-gray-900 mb-2">No Variants Available</h4>
                  <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">Add product variants to manage different colors, sizes, and pricing options for your product.</p>
                  {mode !== 'view' && (
                    <button
                      type="button"
                      onClick={addVariant}
                      className="inline-flex items-center px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all duration-200 ease-in-out hover:shadow-md active:scale-[0.998] font-medium"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create First Variant
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.variants.map((variant, variantIndex) => (
                    <div key={variantIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all duration-200 ease-in-out">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-800 flex items-center">
                          <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded mr-2 font-medium">
                            #{variantIndex + 1}
                          </span>
                          <span className="text-sm">{variant.color || `Variant ${variantIndex + 1}`}</span>
                          <span className="ml-2 text-xs bg-gray-300 text-gray-700 px-2 py-0.5 rounded font-medium">
                            {variant.sizes.length} {variant.sizes.length === 1 ? 'size' : 'sizes'}
                          </span>
                        </h4>
                        {mode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => removeVariant(variantIndex)}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-all duration-200 ease-in-out"
                            title="Remove variant"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      {/* Color Information */}
                      <div className="bg-white rounded border border-gray-200 p-3 mb-3">
                        <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                          <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                          </svg>
                          Color
                        </h5>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Color Name */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Name</label>
                            <input
                              type="text"
                              value={variant.color}
                              onChange={(e) => handleVariantChange(variantIndex, 'color', e.target.value)}
                              disabled={mode === 'view'}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white transition-all duration-200 ease-in-out hover:border-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-100 disabled:bg-gray-50 disabled:text-gray-500"
                              placeholder="Red, Blue, Black"
                            />
                          </div>
                          
                          {/* Color Code */}
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Preview</label>
                            <input
                              type="color"
                              value={variant.color_code || '#ff0000'}
                              onChange={(e) => handleVariantChange(variantIndex, 'color_code', e.target.value)}
                              disabled={mode === 'view'}
                              className="w-full h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-400 transition-all duration-200 ease-in-out focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-100"
                              title="Select color"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sizes Section */}
                      <div className="bg-white rounded border border-gray-200 p-3">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-xs font-medium text-gray-600 flex items-center">
                            <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            Sizes
                          </h5>
                          {mode !== 'view' && (
                            <button
                              type="button"
                              onClick={() => addSizeToVariant(variantIndex)}
                              className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded transition-all duration-200 ease-in-out flex items-center space-x-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span>Add</span>
                            </button>
                          )}
                        </div>

                        {variant.sizes.length === 0 ? (
                          <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <div className="bg-gray-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </div>
                            <p className="text-gray-600 mb-4 font-medium">No sizes added for this variant</p>
                            <p className="text-gray-500 text-sm mb-6">Add different sizes with individual stock and pricing</p>
                            {mode !== 'view' && (
                              <button
                                type="button"
                                onClick={() => addSizeToVariant(variantIndex)}
                                className="inline-flex items-center px-4 py-2.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition-all duration-200 ease-in-out hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.998] font-medium"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add First Size
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {variant.sizes.map((size, sizeIndex) => (
                              <div key={sizeIndex} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-all duration-200 ease-in-out hover:border-gray-300">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded font-medium">
                                      #{sizeIndex + 1}
                                    </span>
                                    <div className={`w-2 h-2 rounded-full ${
                                      (size.stock_quantity || 0) > 0 ? 'bg-green-500' : 'bg-red-500'
                                    }`} title={(size.stock_quantity || 0) > 0 ? 'In Stock' : 'Out of Stock'}></div>
                                  </div>
                                  {mode !== 'view' && (
                                    <button
                                      type="button"
                                      onClick={() => removeSizeFromVariant(variantIndex, sizeIndex)}
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-all duration-200 ease-in-out"
                                      title="Remove size"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  {/* Size Name */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Size</label>
                                    <input
                                      type="text"
                                      value={size.name}
                                      onChange={(e) => handleSizeChange(variantIndex, sizeIndex, 'name', e.target.value)}
                                      disabled={mode === 'view'}
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white transition-all duration-200 ease-in-out hover:border-gray-400 focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100 disabled:bg-gray-50 disabled:text-gray-500"
                                      placeholder="S, M, L"
                                    />
                                  </div>
                                  
                                  {/* Stock Quantity */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Stock</label>
                                    <input
                                      type="number"
                                      value={size.stock_quantity}
                                      disabled={true}
                                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                                      min="0"
                                      placeholder="0"
                                      title="Stock is managed via Inventory → Adjustments & Purchases"
                                    />
                                    <p className="text-xs text-gray-400 mt-0.5">Via Inventory</p>
                                  </div>
                                  
                                  {/* Price */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Price</label>
                                    <input
                                      type="number"
                                      value={size.price || ''}
                                      onChange={(e) => handleSizeChange(variantIndex, sizeIndex, 'price', e.target.value ? Number(e.target.value) : undefined)}
                                      disabled={mode === 'view'}
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white transition-all duration-200 ease-in-out hover:border-gray-400 focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100 disabled:bg-gray-50 disabled:text-gray-500"
                                      min="0"
                                      placeholder="Optional"
                                    />
                                  </div>
                                  
                                  {/* Status */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Active</label>
                                    <div className="flex items-center h-8">
                                      <label className="flex items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={size.is_active}
                                          onChange={(e) => handleSizeChange(variantIndex, sizeIndex, 'is_active', e.target.checked)}
                                          disabled={mode === 'view'}
                                          className="h-3 w-3 text-gray-600 focus:ring-gray-500 border-gray-300 rounded disabled:opacity-50"
                                        />
                                        <span className="ml-1 text-xs text-gray-600">Yes</span>
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Variant Status and Summary */}
                      <div className="bg-white rounded border border-gray-200 p-2 mt-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={variant.is_active}
                                onChange={(e) => handleVariantChange(variantIndex, 'is_active', e.target.checked)}
                                disabled={mode === 'view'}
                                className="h-3 w-3 text-gray-600 focus:ring-gray-500 border-gray-300 rounded disabled:opacity-50"
                              />
                              <span className="ml-2 text-xs font-medium text-gray-700">Active</span>
                            </label>
                            
                            {/* Total Stock for Variant */}
                            <div className="flex items-center bg-gray-50 px-2 py-1 rounded">
                              <div className={`w-2 h-2 rounded-full mr-1 ${
                                variant.sizes.reduce((total, size) => total + (size.stock_quantity || 0), 0) > 0 ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              <span className={`text-xs font-medium ${
                                variant.sizes.reduce((total, size) => total + (size.stock_quantity || 0), 0) > 0 ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {variant.sizes.reduce((total, size) => total + (size.stock_quantity || 0), 0)} units
                              </span>
                            </div>
                          </div>
                          
                          {/* Variant Summary */}
                          <div className="text-right">
                            <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded font-mono">
                              SKU: {variant.sku || 'Auto-generated'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Variant Button - positioned after all variants */}
                  {mode !== 'view' && (
                    <div className="pt-4 border-t border-gray-200 flex justify-center">
                      <button
                        type="button"
                        onClick={addVariant}
                        className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out flex items-center space-x-2 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.998]"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Add Another Variant</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Images */}
          <div className="space-y-8">
            {/* Product Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                <svg className="w-5 h-5 inline mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Product Images
                <span className="text-xs text-gray-500 ml-2">(Multiple images supported)</span>
              </label>
              
              {mode !== 'view' && (
                <div className="mb-6">
                  <div 
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 ${
                      uploadingImages ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
                      if (files.length > 0) {
                        const event = { target: { files } } as any;
                        handleMultipleImageUpload(event);
                      }
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                      onChange={handleMultipleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadingImages}
                    />
                    
                    <div className="space-y-3">
                      {uploadingImages ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="text-blue-600 font-medium mt-2">Uploading images...</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-center">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700">Drop images here or click to browse</p>
                            <p className="text-sm text-gray-500">JPG, PNG, GIF, WebP up to 20MB each</p>
                          </div>
                          <span className="inline-block px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                            Choose Files
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {formData.product_images.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Uploaded Images ({formData.product_images.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {formData.product_images.map((image, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 hover:border-blue-300 transition-colors">
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                        {mode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transform hover:scale-110 transition-all duration-200 shadow-lg"
                            title="Remove image"
                          >
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        {index === 0 && (
                          <div className="absolute bottom-2 left-2">
                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow-sm">
                              Primary
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                  {formData.product_images.length > 1 && (
                    <p className="text-xs text-gray-500 mt-3 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      The first image will be used as the primary product image
                    </p>
                  )}
                </div>
              )}
              {errors.product_images && <p className="text-red-500 text-sm mt-2">{errors.product_images}</p>}
              {errors.hover_image && <p className="text-red-500 text-sm mt-2">{errors.hover_image}</p>}
            </div>

            {/* Hover Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                <svg className="w-5 h-5 inline mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Hover Image
                <span className="text-xs text-gray-500 ml-2">(Shows on product card hover)</span>
              </label>
              
              {mode !== 'view' && (
                <div className="mb-6">
                  <div 
                    className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 hover:border-purple-400 hover:bg-purple-50 ${
                      uploadingHoverImage ? 'border-purple-400 bg-purple-50' : 'border-gray-300 bg-gray-50'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-purple-400', 'bg-purple-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50');
                      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
                      if (files.length > 0) {
                        const event = { target: { files: [files[0]] } } as any;
                        handleHoverImageUpload(event);
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                      onChange={handleHoverImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadingHoverImage}
                    />
                    
                    <div className="space-y-2">
                      {uploadingHoverImage ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                          <p className="text-purple-600 text-sm mt-1">Uploading...</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">Drop hover image here</p>
                            <p className="text-xs text-gray-500">For hover effect (up to 20MB)</p>
                          </div>
                          <span className="inline-block px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors">
                            Choose Image
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {formData.hover_image && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Hover Image Preview
                  </h4>
                  <div className="relative inline-block group">
                    <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 hover:border-purple-300 transition-colors">
                      <img
                        src={formData.hover_image}
                        alt="Hover preview"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    </div>
                    {mode !== 'view' && (
                      <button
                        type="button"
                        onClick={removeHoverImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transform hover:scale-110 transition-all duration-200 shadow-lg"
                        title="Remove hover image"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg"></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    This image will show when customers hover over the product card
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Features and Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Product Features
                </label>
                {mode !== 'view' && (
                  <button
                    type="button"
                    onClick={addFeature}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 transition-all duration-200 ease-in-out"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Feature
                  </button>
                )}
              </div>
              
              {formData.features.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No features added yet</p>
                  {mode !== 'view' && (
                    <button
                      type="button"
                      onClick={addFeature}
                      className="mt-2 inline-flex items-center px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Add First Feature
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                      </div>
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        disabled={mode === 'view'}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder={`Feature ${index + 1}`}
                      />
                      {mode !== 'view' && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="flex-shrink-0 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Remove feature"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {mode === 'view' && formData.features.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  {formData.features.length} feature{formData.features.length !== 1 ? 's' : ''} total
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <textarea
                rows={3}
                value={formData.tags.join(', ')}
                onChange={(e) => handleArrayInputChange('tags', e.target.value)}
                
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] disabled:bg-gray-100"
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>



          {/* Status Checkboxes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">


            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured}
                onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                Featured
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_best_seller"
                checked={formData.is_best_seller}
                onChange={(e) => handleInputChange('is_best_seller', e.target.checked)}
                
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="is_best_seller" className="ml-2 block text-sm text-gray-900">
                Best Seller
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_trending"
                checked={formData.is_trending}
                onChange={(e) => handleInputChange('is_trending', e.target.checked)}
                
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="is_trending" className="ml-2 block text-sm text-gray-900">
                Trending
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="show_on_website"
                checked={formData.show_on_website}
                onChange={(e) => handleInputChange('show_on_website', e.target.checked)}
                
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="show_on_website" className="ml-2 block text-sm text-gray-900">
                Show on Website
              </label>
              <span className="ml-1 text-xs text-gray-500">(POS shows all)</span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (modalMode === 'create' ? 'Create Product' : 'Update Product')}
              </button>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default ProductModal;
